import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import MuiBox from '@material-ui/core/Box';
import MuiChip from '@material-ui/core/Chip';
import MuiButton from '@material-ui/core/Button';
import MuiTypography from '@material-ui/core/Typography';
import FilterListIcon from '@material-ui/icons/FilterList';

const styles = theme => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
    marginBottom: theme.spacing(1),
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(2),
    '& svg': {
      marginRight: theme.spacing(0.5),
    },
  },
  filtersContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    flex: 1,
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  clearButton: {
    marginLeft: 'auto',
  },
});

/**
 * Component to display active filters above the table
 */
const ActiveFilters = props => {
  const { classes, filters, columns, onClearFilter, onClearAllFilters, onClearPersistedFilters } = props;

  // Function to format filter value for display
  const formatFilterValue = (columnId, filterValues) => {
    const column = columns.find(col => col.id === columnId);
    if (!column) return '';

    // Handle direct value (our current structure)
    if (filterValues.value !== undefined) {
      switch (column.filterType) {
        case 'text': {
          const value = filterValues.value.trim();

          // Check if it's multiple values
          const hasMultipleValues =
            /[\s,\n\r]+/.test(value) && value.split(/[\s,\n\r]+/).filter(v => v.trim().length > 0).length > 1;

          if (hasMultipleValues) {
            const values = value
              .split(/[\s,\n\r]+/)
              .map(v => v.trim())
              .filter(v => v.length > 0);
            // Limit of 4 values to show in the chip
            if (values.length <= 4) {
              return values.join(', ');
            } else {
              return `${values.slice(0, 4).join(', ')}... (+${values.length - 4} more)`;
            }
          }

          return value;
        }

        case 'number': {
          const operator = filterValues.operator || 'gte';
          return `${getOperatorLabel(operator)} ${filterValues.value}`;
        }

        case 'date':
          return formatDate(filterValues.value);

        case 'boolean':
          return filterValues.value ? 'Yes' : 'No';

        case 'select': {
          const option = column.options?.find(opt => opt.value === filterValues.value);
          return option ? option.label : filterValues.value;
        }

        default:
          return filterValues.value;
      }
    }

    // Legacy support for complex filter structures
    switch (column.filterType) {
      case 'text': {
        const value = filterValues.value;
        if (!value) return '';

        // Check if it's multiple values
        const hasMultipleValues =
          /[\s,\n\r]+/.test(value) && value.split(/[\s,\n\r]+/).filter(v => v.trim().length > 0).length > 1;

        if (hasMultipleValues) {
          const values = value
            .split(/[\s,\n\r]+/)
            .map(v => v.trim())
            .filter(v => v.length > 0);

          if (values.length <= 4) {
            return values.join(', ');
          } else {
            return `${values.slice(0, 4).join(', ')}... (+${values.length - 4} more)`;
          }
        }

        if (filterValues.operator === 'contains') {
          return value;
        }
        return `${getOperatorLabel(filterValues.operator)}: ${value}`;
      }

      case 'number':
        if (filterValues.operator === 'between') {
          return `Between ${filterValues.min || '?'} and ${filterValues.max || '?'}`;
        }
        return `${getOperatorLabel(filterValues.operator)}: ${filterValues.value}`;

      case 'date':
        // Handle date range filters
        if (filterValues.startDate && filterValues.endDate) {
          return `Between ${formatDate(filterValues.startDate)} and ${formatDate(filterValues.endDate)}`;
        }
        if (filterValues.startDate) {
          return `After ${formatDate(filterValues.startDate)}`;
        }
        if (filterValues.endDate) {
          return `Before ${formatDate(filterValues.endDate)}`;
        }
        return filterValues.value
          ? `${getOperatorLabel(filterValues.operator || 'equals')}: ${formatDate(filterValues.value)}`
          : 'Any date';

      case 'boolean':
        return filterValues.value ? 'Yes' : 'No';

      case 'select': {
        const option = column.options?.find(opt => opt.value === filterValues.value);
        return option ? option.label : filterValues.value;
      }

      default:
        return JSON.stringify(filterValues);
    }
  };

  // Function to format dates
  const formatDate = dateString => {
    if (!dateString) return '?';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US');
    } catch (e) {
      return dateString;
    }
  };

  // Function to get operator label
  const getOperatorLabel = operator => {
    const operators = {
      contains: 'Contains',
      equals: 'Equals',
      startsWith: 'Starts with',
      endsWith: 'Ends with',
      gt: 'Greater than',
      gte: 'Greater than or equal to',
      lt: 'Less than',
      lte: 'Less than or equal to',
      between: 'Between',
      after: 'After',
      before: 'Before',
    };
    return operators[operator] || operator;
  };

  // If there are no active filters, don't render anything
  if (!filters || Object.keys(filters).length === 0) {
    return null;
  }

  return (
    <MuiBox className={classes.root}>
      <MuiTypography variant="body2" className={classes.label}>
        <FilterListIcon fontSize="small" />
        Active filters:
      </MuiTypography>

      <MuiBox className={classes.filtersContainer}>
        {Object.entries(filters).map(([columnId, filterValues]) => {
          const column = columns.find(col => col.id === columnId);
          if (!column) return null;

          return (
            <MuiChip
              key={columnId}
              label={`${column.label}: ${formatFilterValue(columnId, filterValues)}`}
              onDelete={() => onClearFilter(columnId)}
              className={classes.chip}
              size="small"
              color="primary"
              variant="outlined"
            />
          );
        })}
      </MuiBox>

      <MuiButton size="small" onClick={onClearAllFilters} className={classes.clearButton}>
        Clear all
      </MuiButton>

      {/* Debug button to clear persisted filters (development only) */}
      {onClearPersistedFilters && (
        <MuiButton
          size="small"
          onClick={onClearPersistedFilters}
          className={classes.clearButton}
          color="secondary"
          title="Clear persisted filters from localStorage (dev only)">
          Clear Persisted
        </MuiButton>
      )}
    </MuiBox>
  );
};

ActiveFilters.propTypes = {
  classes: PropTypes.object.isRequired,
  filters: PropTypes.object,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      filterType: PropTypes.string.isRequired,
      options: PropTypes.array,
    })
  ).isRequired,
  onClearFilter: PropTypes.func.isRequired,
  onClearAllFilters: PropTypes.func.isRequired,
  onClearPersistedFilters: PropTypes.func, // Optional for development mode
};

ActiveFilters.defaultProps = {
  filters: {},
};

export default withStyles(styles)(ActiveFilters);
